import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Avatar } from "../../../../ui/components/Avatar";
import { FeatherClock, FeatherCheckCircle, FeatherXCircle } from "@subframe/core";
import { ApprovalEntity } from "../../domain/entities/ApprovalEntity";
import { getBankAccountApprovalTitle } from "../../../../shared/utils/bankAccountApprovalUi";
import { formatDateTimeInIST } from "../../../../shared/utils/date";
import { InvoiceBillApprovalCardSummary } from "./InvoiceBillApprovalCardSummary";

interface ApprovalViewCardProps {
  approval: ApprovalEntity;
  onView: () => void;
  id?: string;
}

const ENTITY_TYPE_DISPLAY: Record<string, string> = {
  invoice: "Invoice",
  bill: "Bill",
  customer: "Customer",
  vendor: "Vendor",
  credit_note: "Credit Note",
  debit_note: "Debit Note",
  bank_transaction: "Bank Transaction",
  bank_account: "Bank Account",
  journal: "Journal",
  ap_ar_journal: "AP/AR Journal",
  journal_template: "Recurring Journal",
  gl: "General Ledger",
  month_end_checklist_item: "Month End Checklist",
  tenant_compliance_calendar: "Compliance",
};

const getStatusBadgeVariant = (
  status: string,
): "success" | "error" | "warning" => {
  if (status === "approved") {
    return "success";
  }
  if (status === "rejected") {
    return "error";
  }
  return "warning";
};

const getStatusBadgeText = (status: string): string => {
  if (status === "approved") {
    return "Approved";
  }
  if (status === "rejected") {
    return "Rejected";
  }
  return "Pending";
};

const getStatusIcon = (status: string) => {
  if (status === "approved") {
    return FeatherCheckCircle;
  }
  if (status === "rejected") {
    return FeatherXCircle;
  }
  return FeatherClock;
};

const getBorderColor = (status: string): string => {
  if (status === "approved") {
    return "border-l-success-600";
  }
  if (status === "rejected") {
    return "border-l-error-600";
  }
  return "border-l-warning-600";
};

const formatAmount = (
  rawJson: any,
  entityType?: string,
  changeDetails?: any,
): string => {
  if (!rawJson) return "N/A";

  // Customer and vendor entities don't have amounts
  if (entityType === "customer" || entityType === "vendor") {
    return "N/A";
  }

  // For credit notes, use original_credit_note.credit_note.json.total if available
  if (entityType === "credit_note" && changeDetails?.original_credit_note) {
    const originalCreditNote = changeDetails.original_credit_note.credit_note;
    if (originalCreditNote?.json) {
      const creditNoteJson =
        typeof originalCreditNote.json === "string"
          ? JSON.parse(originalCreditNote.json)
          : originalCreditNote.json;
      const amount = creditNoteJson.total || 0;
      const currencySymbol = creditNoteJson.currency_symbol || "₹";
      return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }

  // For bank transactions, use categorize_in_zoho_json if available
  if (entityType === "bank_transaction" && rawJson.categorize_in_zoho_json) {
    const categorizeData = rawJson.categorize_in_zoho_json;
    const amount =
      categorizeData.amount ||
      rawJson.transaction_amount ||
      rawJson.amount ||
      0;
    const currencySymbol =
      categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
    if (amount === 0) return "N/A";
    return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // For bank transactions without categorize_in_zoho_json, use transaction_amount
  if (entityType === "bank_transaction") {
    const amount = rawJson.transaction_amount || rawJson.amount || 0;
    const currencySymbol = rawJson.currency_symbol || "₹";
    if (amount === 0) return "N/A";
    return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // Try to get amount from different possible fields
  const amount =
    rawJson.total ||
    rawJson.amount ||
    rawJson.sub_total ||
    rawJson.total_amount ||
    0;

  const currencySymbol = rawJson.currency_symbol || "USD";

  return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatDateFull = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    // Try to parse the date
    const date = new Date(dateString);

    // Format as DD-MM-YYYY
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

const getReferenceNumber = (rawJson: any, entityType: string): string => {
  if (!rawJson) return "N/A";

  switch (entityType) {
    case "invoice":
      return rawJson.invoice_number || "N/A";
    case "bill":
      return rawJson.bill_number || rawJson.vendor_bill_number || "N/A";
    case "customer":
      return rawJson.customer_name || rawJson.customerid || "N/A";
    case "vendor":
      return (
        rawJson.vendor_legal_name ||
        rawJson.vendor_name ||
        rawJson.vendorid ||
        "N/A"
      );
    case "credit_note":
      return (
        rawJson.creditnote_number ||
        rawJson.credit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "debit_note":
      return (
        rawJson.debitnote_number ||
        rawJson.debit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "journal":
    case "ap_ar_journal":
      return rawJson.journal_number || rawJson.reference_number || "N/A";
    case "bank_transaction":
      return rawJson.transaction_id || rawJson.reference_number || "N/A";
    default:
      return "N/A";
  }
};

// This component is used for "My Requests" view - read-only with status display
export const ApprovalViewCard: React.FC<ApprovalViewCardProps> = ({
  approval,
  onView,
  id,
}) => {
  const rawJson = approval.changeDetails?.raw_json;
  const actionTypes = approval.changeDetails?.action_types || [];
  const isGlBulkUpload =
    approval.entityType === "gl" &&
    (actionTypes.includes("bulk_upload") ||
      (approval.actionCode ?? "").toLowerCase() === "bulk_upload");
  const StatusIcon = getStatusIcon(approval.status);

  return (
    <div
      id={id || `approval-${approval.id}`}
      className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background shadow-sm ${getBorderColor(
        approval.status,
      )}`}
    >
      <div className="flex w-full items-start justify-between gap-4 px-6 pt-6 pb-4">
        <div className="flex flex-col items-start gap-4 flex-1 min-w-0">
          {/* Header with Entity Type and Status Badge */}
          <div className="flex items-start gap-3 flex-wrap w-full">
            {approval.entityType === "month_end_checklist_item" ? (
              <>
                <span className="text-body font-body text-subtext-color">
                  Month End Checklist
                </span>
                <span className="text-body font-body text-subtext-color">
                  →
                </span>
                <span className="text-body font-body text-default-font">
                  {approval.accountingArea || "N/A"}
                </span>
              </>
            ) : approval.entityType === "bank_transaction" ? (
              <span className="whitespace-nowrap text-heading-3 font-heading-3 text-default-font">
                Bank Transaction - {approval.actionName}
              </span>
            ) : approval.entityType === "bank_account" ? (
              <span className="whitespace-nowrap text-heading-3 font-heading-3 text-default-font">
                {getBankAccountApprovalTitle(approval.actionCode, rawJson)}
                {rawJson?.account_name
                  ? ` – ${String(rawJson.account_name)}`
                  : ""}
              </span>
            ) : (
              <span className="text-heading-3 font-heading-3 text-default-font">
                {(() => {
                  const entityName =
                    ENTITY_TYPE_DISPLAY[approval.entityType] ||
                    approval.entityType;

                  // Get reference number
                  let refNumber: string | null = null;
                  if (
                    approval.entityType === "journal" ||
                    approval.entityType === "ap_ar_journal"
                  ) {
                    refNumber =
                      rawJson?.journal_number ||
                      rawJson?.reference_number ||
                      null;
                  } else if (approval.entityType === "journal_template") {
                    refNumber = rawJson?.template_name || null;
                  } else if (
                    (approval.entityType === "customer" ||
                      approval.entityType === "vendor" ||
                      approval.entityType === "gl") &&
                    (approval.actionCode ?? "").toLowerCase() === "bulk_upload"
                  ) {
                    const rows = rawJson?.rows;
                    const n = Array.isArray(rows) ? rows.length : 0;
                    refNumber =
                      n > 0 ? `${n} row${n === 1 ? "" : "s"}` : "Bulk upload";
                  } else if (
                    approval.entityType !== "bank_transaction" &&
                    approval.entityType !== "month_end_checklist_item"
                  ) {
                    const refSourceJson =
                      approval.entityType === "invoice" ||
                      approval.entityType === "bill"
                        ? approval.changeDetails?.raw_json ?? rawJson
                        : rawJson;
                    const ref = getReferenceNumber(
                      refSourceJson,
                      approval.entityType,
                    );
                    refNumber = ref && ref !== "N/A" ? ref : null;
                  }

                  // Build the header string
                  let header = entityName;
                  if (refNumber) {
                    header += ` - ${refNumber}`;
                  }
                  // For journal, append friendly action label for ap_ar_create / ap_ar_delete
                  if (
                    approval.entityType === "journal" ||
                    approval.entityType === "ap_ar_journal"
                  ) {
                    const apArLabel = actionTypes.includes("ap_ar_create")
                      ? "AP/AR Journal – Create"
                      : actionTypes.includes("ap_ar_delete")
                        ? "AP/AR Journal – Delete"
                        : null;
                    if (apArLabel) {
                      header += ` (${apArLabel})`;
                    }
                  }
                  // For invoice, append friendly action label for write_off / cancel_write_off
                  if (approval.entityType === "invoice") {
                    const writeOffLabel = actionTypes.includes("write_off")
                      ? "Write off"
                      : actionTypes.includes("cancel_write_off")
                        ? "Cancel write off"
                        : null;
                    if (writeOffLabel) {
                      header += ` (${writeOffLabel})`;
                    }
                  }
                  if (
                    (approval.entityType === "customer" ||
                      approval.entityType === "vendor" ||
                      approval.entityType === "gl") &&
                    actionTypes.includes("bulk_upload")
                  ) {
                    const rows = rawJson?.rows;
                    const n = Array.isArray(rows) ? rows.length : 0;
                    header += n > 0 ? ` (${n} rows)` : " (Bulk upload)";
                  }

                  return header;
                })()}
              </span>
            )}
            <Badge variant={getStatusBadgeVariant(approval.status)}>
              {getStatusBadgeText(approval.status)}
            </Badge>
            {(approval.changeDetails?.closed_month === true ||
              actionTypes?.includes("month_close")) && (
              <span className="text-caption font-caption text-neutral-600 rounded-md bg-neutral-100 px-2 py-1 border border-solid border-neutral-200">
                Post-close change
              </span>
            )}
          </div>

          {/* Pending: show manager/approver the request was sent to */}
          {approval.status === "pending" && approval.reviewerName && (
            <div className="flex items-center gap-3 rounded-lg bg-neutral-50 px-4 py-3 border border-solid border-neutral-200 w-full">
              <FeatherClock className="text-[20px] text-warning-600" />
              <span className="text-body font-body text-default-font">
                Request sent to{" "}
                <span className="font-semibold text-body-bold">
                  {approval.reviewerName}
                </span>
              </span>
            </div>
          )}

          {/* Status Details - Show reviewer info if approved/rejected */}
          {(approval.status === "approved" || approval.status === "rejected") &&
            approval.reviewerName && (
              <div className="flex items-center gap-3 rounded-lg bg-neutral-50 px-4 py-3 border border-solid border-neutral-200 w-full">
                <StatusIcon
                  className={`text-[20px] ${
                    approval.status === "approved"
                      ? "text-success-600"
                      : "text-error-600"
                  }`}
                />
                <span className="text-body font-body text-default-font">
                  {approval.status === "approved" ? "Approved" : "Rejected"} by{" "}
                  <span className="font-semibold text-body-bold">
                    {approval.reviewerName}
                  </span>
                  {approval.reviewedAt && (
                    <span className="text-subtext-color">
                      {" "}
                      • {formatDateTimeInIST(approval.reviewedAt)}
                    </span>
                  )}
                </span>
              </div>
            )}

          {/* Show comments if present */}
          {approval.reviewComments && (
            <div className="flex flex-col gap-2 rounded-lg bg-info-50 px-4 py-3 border border-solid border-info-200 w-full">
              <span className="text-caption font-caption text-info-700 font-semibold uppercase tracking-wide">
                {approval.status === "approved"
                  ? "Approval Note"
                  : "Rejection Reason"}
              </span>
              <span className="text-body font-body text-info-900">
                {approval.reviewComments}
              </span>
            </div>
          )}

          {/* Request comments – same styled box for all entity types */}
          {approval.requestComments && (
            <div className="flex flex-col gap-2 rounded-lg bg-info-50 px-4 py-3 border border-solid border-info-200 w-full">
              <span className="text-caption font-caption text-info-700 font-semibold uppercase tracking-wide">
                Accountant's Comments
              </span>
              <span className="text-body font-body text-info-900">
                {approval.requestComments}
              </span>
            </div>
          )}

          {/* Entity-specific details */}
          {approval.entityType === "invoice" ||
          approval.entityType === "bill" ? (
            <InvoiceBillApprovalCardSummary
              kind={
                approval.entityType === "invoice" ? "invoice" : "bill"
              }
              changeDetails={approval.changeDetails}
              variant="emphasized"
            />
          ) : approval.entityType === "bank_transaction" ? (
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Transaction Date:
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {formatDateFull(
                    approval.changeDetails?.raw_json?.date ||
                      approval.changeDetails?.raw_json?.transaction_date ||
                      (approval.changeDetails?.raw_json?.categorize_in_zoho_json
                        ?.date
                        ? approval.changeDetails.raw_json
                            .categorize_in_zoho_json.date
                        : undefined),
                  ) || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Transaction Amount:
                </span>
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {approval.changeDetails?.raw_json?.amount ||
                  approval.changeDetails?.raw_json?.transaction_amount ||
                  approval.changeDetails?.raw_json?.categorize_in_zoho_json
                    ?.amount
                    ? formatAmount(
                        approval.changeDetails.raw_json,
                        approval.entityType,
                        approval.changeDetails,
                      )
                    : "N/A"}
                </span>
              </div>
            </div>
          ) : approval.entityType === "bank_account" && rawJson ? (
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account type:
                </span>
                <span className="text-body font-body text-default-font">
                  {rawJson.account_type === "credit_card" ||
                  rawJson.account_type === "credit"
                    ? "Credit card"
                    : "Bank account"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account name:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(rawJson.account_name ?? "—")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account number:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(rawJson.account_number ?? "—")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Currency:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(
                    rawJson.currency_code ?? rawJson.currency_id ?? "—",
                  )}
                </span>
              </div>
            </div>
          ) : approval.entityType === "gl" && rawJson && !isGlBulkUpload ? (
            <div className="flex flex-col items-start gap-2 w-full">
              {(() => {
                const glActionTypes = approval.changeDetails?.action_types || [];
                const glAction = glActionTypes.includes("delete")
                  ? "Delete GL"
                  : glActionTypes.includes("edit") || glActionTypes.includes("update")
                    ? "Update GL"
                    : "Create a GL";
                const isDelete = glActionTypes.includes("delete");
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">Action:</span>
                      <span className={`text-body font-body font-semibold ${isDelete ? "text-error-600" : "text-default-font"}`}>
                        {glAction}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">Account name:</span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_name ?? "—")}
                      </span>
                    </div>
                    {rawJson.account_type && (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-subtext-color">Account type:</span>
                        <span className="text-body font-body text-default-font">
                          {String(rawJson.account_type)
                            .split("_")
                            .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(" ")}
                        </span>
                      </div>
                    )}
                    {rawJson.parent_account_name && (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-subtext-color">Parent account:</span>
                        <span className="text-body font-body text-default-font">
                          {String(rawJson.parent_account_name)}
                        </span>
                      </div>
                    )}
                    {!isDelete && (() => {
                      const usedIn: string[] = [];
                      if (rawJson.invoice) usedIn.push("Invoices");
                      if (rawJson.bill) usedIn.push("Bills");
                      return usedIn.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <span className="text-body font-body text-subtext-color">Use in:</span>
                          <span className="text-body font-body text-default-font">
                            {usedIn.join(" & ")}
                          </span>
                        </div>
                      ) : null;
                    })()}
                  </>
                );
              })()}
            </div>
          ) : approval.entityType === "month_end_checklist_item" ? (
            <div className="flex flex-col items-start gap-3 w-full">
              <span className="text-body-bold font-body-bold text-default-font">
                {approval.itemTitle || "Checklist Item"}
              </span>
              {approval.itemDescription && (
                <span className="text-body font-body text-default-font">
                  {approval.itemDescription}
                </span>
              )}
              {(() => {
                const requestText =
                  approval.actionCode === "to_be_done_later"
                    ? "Request: Mark this checklist item as To be done later."
                    : approval.actionCode === "mark_as_done" ||
                        approval.actionCode === "marked_to_done" ||
                        approval.actionCode === "accountant_checklist"
                      ? "Request: Mark this checklist item as done."
                      : null;

                return requestText ? (
                  <span className="text-body font-body text-info-700">
                    {requestText}
                  </span>
                ) : null;
              })()}
            </div>
          ) : null}
        </div>

        {/* View Button */}
        <Button
          variant="neutral-secondary"
          size="small"
          onClick={onView}
          className="flex-shrink-0"
        >
          View
        </Button>
      </div>
      {/* Divider */}
      <div className="flex h-px w-full flex-none bg-neutral-border" />

      {/* Footer Section */}
      <div className="flex w-full items-center justify-between px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <FeatherClock className="text-[16px] text-neutral-400" />
            <span className="text-body font-body text-subtext-color">
              {approval.status === "approved" || approval.status === "rejected"
                ? `Modified ${formatDateTimeInIST(
                    approval.reviewedAt ||
                      approval.updatedAt ||
                      approval.createdAt,
                  )}`
                : `Raised ${formatDateTimeInIST(approval.createdAt)}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Avatar variant="brand" size="x-small" image="">
              {approval.requesterName
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase() || "U"}
            </Avatar>
            <span className="text-body font-body text-subtext-color">
              Requested by you
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
